import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Pagination,
  Select,
  type BadgeTone,
  type Column,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import {
  useOnlinePayments,
  useResolveOnlinePayment,
} from '@/features/online-payments/api';
import {
  ONLINE_PAYMENT_STATUSES,
  type OnlinePaymentReview,
  type OnlinePaymentsQuery,
  type OnlinePaymentResolutionType,
  type OnlinePaymentStatus,
} from '@/features/online-payments/types';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './OnlinePaymentsPage.module.css';

type QueueFilter = 'all' | 'needs_review';
type FilterDraft = {
  status: OnlinePaymentStatus | '';
  billId: string;
  from: string;
  to: string;
};

const EMPTY_FILTERS: FilterDraft = {
  status: '',
  billId: '',
  from: '',
  to: '',
};

const STATUS_TONE: Record<OnlinePaymentStatus, BadgeTone> = {
  pending: 'info',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  needs_review: 'warning',
};

export function OnlinePaymentsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const [queue, setQueue] = useState<QueueFilter>('all');
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<FilterDraft>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<OnlinePaymentReview | null>(null);
  const query: OnlinePaymentsQuery = {
    page,
    ...(queue === 'needs_review' ? { needsAction: true } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.billId ? { billId: filters.billId } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };
  const payments = useOnlinePayments(query);
  const canResolve = hasPermission('online_payments:resolve');

  const statusLabel = (status: OnlinePaymentStatus) =>
    t(`onlinePayments.statuses.${status}`);
  const methodLabel = (method: OnlinePaymentReview['method']) =>
    method === 'e_wallet'
      ? t('onlinePayments.methods.grabPay')
      : method === 'card'
        ? t('onlinePayments.methods.card')
        : t('onlinePayments.methods.unknown');
  const providerLabel = (provider: string) =>
    provider === 'stripe'
      ? t('onlinePayments.providers.stripe')
      : provider.charAt(0).toUpperCase() + provider.slice(1);
  const reasonLabel = (reason: string | null) =>
    reason
      ? t(`onlinePayments.reviewReasons.${reason}`, {
          defaultValue: reason.replace(/_/g, ' '),
        })
      : t('common.notAvailable');

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters({
      status: draft.status,
      billId: draft.billId.trim(),
      from: draft.from,
      to: draft.to,
    });
    setPage(1);
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const columns: Column<OnlinePaymentReview>[] = [
    {
      key: 'created',
      header: t('onlinePayments.created'),
      render: (payment) => (
        <>
          <span className={styles.primaryText}>
            {new Date(payment.createdAt).toLocaleDateString('en-GB')}
          </span>
          <span className={styles.secondaryText}>
            {new Date(payment.createdAt).toLocaleTimeString('en-GB')}
          </span>
        </>
      ),
    },
    {
      key: 'payment',
      header: t('onlinePayments.payment'),
      render: (payment) => (
        <>
          <span className={styles.primaryText}>
            {providerLabel(payment.provider)} · {methodLabel(payment.method)}
          </span>
          <span className={`${styles.secondaryText} ${styles.mono}`}>
            {payment.providerRef}
          </span>
        </>
      ),
    },
    {
      key: 'bill',
      header: t('onlinePayments.bill'),
      render: (payment) => (
        <>
          <span className={styles.primaryText}>{payment.billNumber}</span>
          <span className={styles.secondaryText}>
            {t('onlinePayments.billSummary', {
              status: t(`onlinePayments.billStatuses.${payment.billStatus}`),
              balance: formatRM(payment.billBalanceSen),
            })}
          </span>
        </>
      ),
    },
    {
      key: 'patient',
      header: t('onlinePayments.patient'),
      render: (payment) => (
        <>
          <span className={styles.primaryText}>{payment.patient.fullName}</span>
          <span className={styles.secondaryText}>{payment.branch.name}</span>
        </>
      ),
    },
    {
      key: 'amount',
      header: t('onlinePayments.amount'),
      align: 'right',
      render: (payment) => formatRM(payment.amountSen),
    },
    {
      key: 'status',
      header: t('onlinePayments.status'),
      render: (payment) => (
        <>
          <Badge tone={STATUS_TONE[payment.status]}>
            {statusLabel(payment.status)}
          </Badge>
          {(payment.reviewReason || payment.failureCode) && (
            <span className={styles.secondaryText}>
              {payment.reviewReason
                ? reasonLabel(payment.reviewReason)
                : payment.failureCode}
            </span>
          )}
          {payment.resolution && (
            <span className={styles.secondaryText}>
              {t(`onlinePayments.resolutions.${payment.resolution.type}`)}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'actions',
      header: t('onlinePayments.actions'),
      render: (payment) =>
        payment.needsAction && canResolve ? (
          <div className={styles.rowActions}>
            <Button size="sm" onClick={() => setSelected(payment)}>
              {t('onlinePayments.resolve')}
            </Button>
          </div>
        ) : (
          <span className={styles.secondaryText}>
            {payment.needsAction
              ? t('onlinePayments.resolvePermissionRequired')
              : t('onlinePayments.noActionRequired')}
          </span>
        ),
    },
  ];

  return (
    <PageContainer
      actions={
        <ChipToggle<QueueFilter>
          label={t('onlinePayments.queueFilter')}
          single
          selected={[queue]}
          options={[
            { value: 'all', label: t('onlinePayments.allPayments') },
            {
              value: 'needs_review',
              label: t('onlinePayments.needsReview'),
            },
          ]}
          onChange={(next) => {
            if (!next[0]) return;
            setQueue(next[0]);
            setPage(1);
          }}
        />
      }
    >
      <Card title={t('onlinePayments.title')} flush>
        <form className={styles.filters} onSubmit={applyFilters}>
          <Field label={t('onlinePayments.status')}>
            <Select
              value={draft.status}
              placeholder={t('onlinePayments.allStatuses')}
              options={ONLINE_PAYMENT_STATUSES.map((status) => ({
                value: status,
                label: statusLabel(status),
              }))}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  status: event.target.value as FilterDraft['status'],
                }))
              }
            />
          </Field>
          <Field label={t('onlinePayments.billId')}>
            <Input
              value={draft.billId}
              placeholder={t('onlinePayments.billIdPlaceholder')}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  billId: event.target.value,
                }))
              }
            />
          </Field>
          <Field label={t('onlinePayments.from')}>
            <Input
              type="date"
              value={draft.from}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  from: event.target.value,
                }))
              }
            />
          </Field>
          <Field label={t('onlinePayments.to')}>
            <Input
              type="date"
              value={draft.to}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  to: event.target.value,
                }))
              }
            />
          </Field>
          <div className={styles.filterActions}>
            <Button type="submit" variant="primary">
              {t('onlinePayments.applyFilters')}
            </Button>
            <Button type="button" variant="ghost" onClick={resetFilters}>
              {t('onlinePayments.clearFilters')}
            </Button>
          </div>
        </form>
        <DataTable
          caption={t('onlinePayments.title')}
          columns={columns}
          rows={payments.data?.data}
          rowKey={(payment) => payment.paymentId}
          loading={payments.isLoading}
          error={payments.error}
          onRetry={() => void payments.refetch()}
          empty={
            <EmptyState
              title={
                queue === 'needs_review'
                  ? t('onlinePayments.noReviewPayments')
                  : t('onlinePayments.noPayments')
              }
              body={
                queue === 'needs_review'
                  ? t('onlinePayments.noReviewPaymentsBody')
                  : t('onlinePayments.noPaymentsBody')
              }
            />
          }
          footer={
            payments.data?.meta && (
              <Pagination meta={payments.data.meta} onPageChange={setPage} />
            )
          }
        />
      </Card>

      {selected && (
        <ResolvePaymentDialog
          payment={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </PageContainer>
  );
}

function ResolvePaymentDialog({
  payment,
  onClose,
}: {
  payment: OnlinePaymentReview;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const resolvePayment = useResolveOnlinePayment();
  const [resolution, setResolution] =
    useState<OnlinePaymentResolutionType>('applied');
  const [note, setNote] = useState('');
  const [refundRef, setRefundRef] = useState('');
  const validRefundRef = /^re_[A-Za-z0-9_]{6,70}$/.test(refundRef.trim());
  const valid =
    note.trim().length > 0 &&
    note.trim().length <= 500 &&
    (resolution === 'applied' || validRefundRef);

  const submit = () => {
    if (!valid) return;
    resolvePayment.mutate(
      {
        paymentId: payment.paymentId,
        resolution,
        note: note.trim(),
        ...(resolution === 'refunded' ? { refundRef: refundRef.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: t(`onlinePayments.resolvedToast.${resolution}`),
          });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      variant="modal"
      title={t('onlinePayments.resolveTitle', {
        billNumber: payment.billNumber,
      })}
      onClose={onClose}
      dismissible={!resolvePayment.isPending}
      footer={
        <>
          <Button onClick={onClose} disabled={resolvePayment.isPending}>
            {t('onlinePayments.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={resolvePayment.isPending}
            onClick={submit}
          >
            {t('onlinePayments.confirmResolution')}
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        <dl className={styles.facts}>
          <dt>{t('onlinePayments.patient')}</dt>
          <dd>{payment.patient.fullName}</dd>
          <dt>{t('onlinePayments.amount')}</dt>
          <dd>{formatRM(payment.amountSen)}</dd>
          <dt>{t('onlinePayments.providerReference')}</dt>
          <dd className={styles.mono}>{payment.providerRef}</dd>
          <dt>{t('onlinePayments.reviewReason')}</dt>
          <dd>
            {payment.reviewReason
              ? t(`onlinePayments.reviewReasons.${payment.reviewReason}`, {
                  defaultValue: payment.reviewReason.replace(/_/g, ' '),
                })
              : t('common.notAvailable')}
          </dd>
        </dl>

        <Field label={t('onlinePayments.resolution')}>
          <Select
            value={resolution}
            options={[
              {
                value: 'applied',
                label: t('onlinePayments.resolutions.applied'),
              },
              {
                value: 'refunded',
                label: t('onlinePayments.resolutions.refunded'),
              },
            ]}
            onChange={(event) => {
              setResolution(event.target.value as OnlinePaymentResolutionType);
              resolvePayment.reset();
            }}
          />
        </Field>

        {resolution === 'refunded' && (
          <>
            <p className={styles.refundNotice}>
              {t('onlinePayments.refundNotice')}
            </p>
            <Field
              label={t('onlinePayments.refundReference')}
              hint={t('onlinePayments.refundReferenceHint')}
              error={
                refundRef.length > 0 && !validRefundRef
                  ? t('onlinePayments.refundReferenceInvalid')
                  : undefined
              }
            >
              <Input
                value={refundRef}
                placeholder={t('onlinePayments.refundReferencePlaceholder')}
                onChange={(event) => setRefundRef(event.target.value)}
              />
            </Field>
          </>
        )}

        <Field
          label={t('onlinePayments.note')}
          hint={t('onlinePayments.noteHint', { count: note.length })}
          error={
            note.length > 500 ? t('onlinePayments.noteTooLong') : undefined
          }
        >
          <textarea
            className={styles.textarea}
            value={note}
            maxLength={501}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {resolvePayment.error && (
          <ErrorState error={resolvePayment.error} onRetry={null} />
        )}
      </div>
    </Dialog>
  );
}
